import React, { useState } from 'react';
import { MobileHomeHeader } from '../MobileHomeHeader';
import { LocationModal } from '../modals/LocationModal';
import { PageHeroBreadcrumb } from '../layout/PageHeroBreadcrumb';
interface AccountLayoutProps {
  children: React.ReactNode;
  activeSection: string;
}
const SECTION_META: Record<string, {
  title: string;
  description: string;
}> = {
  overview: {
    title: 'My Account',
    description: 'Manage your profile, orders, addresses, wallet, loyalty points, and more — all in one place.'
  },
  'login-security': {
    title: 'Login & Security',
    description: 'Update your password, manage two-factor authentication, and review account security settings.'
  },
  addresses: {
    title: 'My Addresses',
    description: 'Save and manage your delivery addresses for faster checkout across Innova Pharmacy.'
  },
  orders: {
    title: 'My Orders',
    description: 'Track current orders, review past purchases, and reorder your favorite products in a tap.'
  },
  wallet: {
    title: 'My Wallet',
    description: 'View your balance, top up funds, and review wallet transactions used at Innova Pharmacy.'
  },
  'loyalty-points': {
    title: 'Loyalty Points',
    description: 'Earn points on every purchase and redeem them for discounts and exclusive rewards.'
  },
  wishlist: {
    title: 'My Wishlist',
    description: 'Keep track of products you love and move them to your cart whenever you’re ready.'
  },
  returns: {
    title: 'Returns',
    description: 'Request returns, track refund status, and review your return history with Innova Pharmacy.'
  },
  coupons: {
    title: 'My Coupons',
    description: 'Browse available coupons and promo codes you can apply at checkout for instant savings.'
  },
  'payment-requests': {
    title: 'Payment Requests',
    description: 'Review and manage payment requests linked to your Innova Pharmacy account.'
  },
  'payment-request-details': {
    title: 'Payment Request Details',
    description: 'Review the details of your payment request, including amount, status, and history.'
  }
};
const SECTION_BREADCRUMB_LABELS: Record<string, string> = {
  overview: 'My Account',
  'login-security': 'Login & Security',
  addresses: 'Addresses',
  orders: 'Orders',
  wallet: 'Wallet',
  'loyalty-points': 'Loyalty Points',
  wishlist: 'Wishlist',
  returns: 'Returns',
  coupons: 'Coupons',
  'payment-requests': 'Payment Requests',
  'payment-request-details': 'Payment Request Details'
};
export function AccountLayout({
  children,
  activeSection
}: AccountLayoutProps) {
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const meta = SECTION_META[activeSection] || SECTION_META.overview;
  const isOverview = activeSection === 'overview' || !SECTION_META[activeSection];
  const breadcrumbs = isOverview ? [{
    label: 'Home',
    href: '/'
  }, {
    label: 'My Account'
  }] : [{
    label: 'Home',
    href: '/'
  }, {
    label: 'My Account',
    href: '/account'
  }, {
    label: SECTION_BREADCRUMB_LABELS[activeSection] || meta.title
  }];
  return <div className="min-h-screen" data-id="element-104" data-mp="3n6ei28haid67">
      {/* Mobile Header - Only on mobile */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-105" data-mp="3i7uts1f84pao">
        <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-106" data-mp="3i7l4kg206m61" />
      </div>
      {/* Mobile-only top padding (desktop padding is applied by parent page's <main>) */}
      <div className="pt-[var(--header-height-mobile)] md:pt-0" data-id="element-107" data-mp="9sjj9axcx6eq">
        {/* Page Hero Breadcrumb */}
        <PageHeroBreadcrumb breadcrumbs={breadcrumbs} title={meta.title} description={meta.description} data-id="element-108" data-mp="4ui3ayv89n7h" />
        <div className="container mx-auto max-w-5xl px-4 py-4 md:py-6" data-id="element-109" data-mp="swenmy7g0o0e">
          <div role="main" id={`panel-${activeSection}`} data-id="element-110" data-mp="sujzlb0vkfs3">{children}</div>
        </div>
      </div>
      {/* Location Modal */}
      <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
      setSelectedLocation(location);
      setLocationModalOpen(false);
    }} data-id="element-111" data-mp="12m6lc1au9x93" />
    </div>;
}